/**
 * Centralised resolution of bound values (literal, $param, $cases).
 * Port of the shared module content-builder/shared/contextual-value/resolve-bound-value.js
 */

export interface ContextualCases {
  $cases: Array<{
    when?: Array<{ type: string; options?: Record<string, unknown> }>;
    value?: unknown;
  }>;
  default?: unknown;
}

export interface RuntimeContextSnapshot {
  locale: string;
  device: string;
  isAuthenticated: boolean;
  weekday: number;
  minuteOfDay: number;
  date: string;
  params: Record<string, unknown>;
  isTestMode: boolean;
  /** Current page shop id (route-bound), when known. */
  shopId: string | null;
}

export interface ResolveBoundValueOptions {
  context?: RuntimeContextSnapshot;
  lang?: string;
  resolveParamRef?: (refKey: string) => unknown;
  maxDepth?: number;
  depth?: number;
  /** Leaves `$cases` untouched for runtime resolution (dood-front preview). */
  preserveContextualCases?: boolean;
}

const MAX_DEPTH_DEFAULT = 32;

function whenRecordToConditions(
  dimension: string,
  when: Record<string, unknown>,
): Array<{ type: string; options?: Record<string, unknown> }> {
  const entries = Object.entries(when).filter(([key]) => key !== '$kind');
  if (entries.length === 0) {
    return [];
  }

  const type =
    typeof when['$kind'] === 'string' && when['$kind'].trim() ? String(when['$kind']) : dimension;
  const { $kind: _kind, ...options } = when;
  return [{ type, options }];
}

/**
 * Converts CB2 studio `$cases` ({ dimension, branches }) to the runtime array shape
 * understood by `selectContextualBranch`. Preview payloads are normalized upstream;
 * compiled live specs may still carry the studio object payload.
 */
export function normalizeStudioCasesToRuntime(value: unknown): unknown {
  if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  if (!('$cases' in (value as object))) {
    return value;
  }

  const root = value as Record<string, unknown>;
  const casesPayload = root.$cases;

  if (Array.isArray(casesPayload)) {
    return value;
  }

  if (!casesPayload || typeof casesPayload !== 'object') {
    return value;
  }

  const obj = casesPayload as {
    dimension?: string;
    branches?: ReadonlyArray<{
      when?: Record<string, unknown>;
      value?: unknown;
      label?: string;
    }>;
    default?: unknown;
  };
  const dimension = obj.dimension ?? 'locale';
  const branches = (obj.branches ?? []).map(branch => ({
    when: whenRecordToConditions(dimension, branch.when ?? {}),
    value: branch.value,
    ...(typeof branch.label === 'string' && branch.label.trim()
      ? { label: branch.label.trim() }
      : {}),
  }));

  if (branches.length === 0) {
    return obj.default ?? value;
  }

  return {
    $cases: branches,
    default: obj.default,
  };
}

export function isContextualCases(value: unknown): value is ContextualCases {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    '$cases' in (value as object) &&
    Array.isArray((value as ContextualCases).$cases)
  );
}

export function isParamRefValue(value: unknown): value is { $param: string } | { $var: string } {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('$param' in (value as object) || '$var' in (value as object))
  );
}

export function readParamRefKeyFromValue(value: unknown): string {
  if (!isParamRefValue(value)) return '';
  const obj = value as { $param?: string; $var?: string };
  const k = obj.$param ?? obj.$var;
  return typeof k === 'string' ? k.trim() : '';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatContextDate(date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function buildRuntimeContextSnapshot(
  partial: Partial<RuntimeContextSnapshot> & { now?: Date | string | number } = {},
): RuntimeContextSnapshot {
  const now =
    partial.now instanceof Date
      ? partial.now
      : partial.now != null
        ? new Date(partial.now)
        : new Date();

  return {
    locale: partial.locale ?? 'fr',
    device: partial.device ?? 'desktop',
    isAuthenticated: partial.isAuthenticated ?? false,
    weekday: partial.weekday ?? now.getDay(),
    minuteOfDay: partial.minuteOfDay ?? now.getHours() * 60 + now.getMinutes(),
    date: partial.date ?? formatContextDate(now),
    params: partial.params ?? {},
    isTestMode: partial.isTestMode ?? false,
    shopId: partial.shopId ?? null,
  };
}

function readShopIdsFromConditionOptions(
  opt: Record<string, unknown>,
): string[] {
  const selected = opt['selectedShops'];
  if (!Array.isArray(selected)) {
    return [];
  }
  return selected
    .map(entry => {
      if (entry && typeof entry === 'object' && 'id' in (entry as object)) {
        const id = (entry as { id?: unknown }).id;
        return typeof id === 'string' && id.trim() ? id.trim() : '';
      }
      return '';
    })
    .filter(Boolean);
}

function evaluateShopContextCondition(
  opt: Record<string, unknown>,
  snapshot: RuntimeContextSnapshot,
): boolean {
  const shopIds = readShopIdsFromConditionOptions(opt);
  if (shopIds.length === 0) {
    return false;
  }

  const isOrNot = opt['isOrNot'] === 'isNot' ? 'isNot' : 'is';
  const shopId = snapshot.shopId;
  if (!shopId) {
    return isOrNot === 'isNot';
  }

  const isIncluded = shopIds.includes(shopId);
  return isOrNot === 'isNot' ? !isIncluded : isIncluded;
}

function parseHm(raw: unknown, fallback: string): number {
  const s = typeof raw === 'string' && raw.trim() ? raw.trim() : fallback;
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function minuteInRange(minute: number, startM: number, endM: number): boolean {
  if (startM <= endM) return minute >= startM && minute <= endM;
  return minute >= startM || minute <= endM;
}

function dateInRange(dateStr: string, start: string, end: string): boolean {
  if (!start && !end) return true;
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
}

export function evaluateContextCondition(
  condition: { type: string; options?: Record<string, unknown> },
  snapshot: RuntimeContextSnapshot,
): boolean {
  const type = condition?.type ?? '';
  const opt = condition?.options ?? {};

  switch (type) {
    case 'alwaysVisible':
      return true;
    case 'neverVisible':
      return false;
    case 'locale': {
      const list = Array.isArray(opt['in']) ? (opt['in'] as string[]) : [];
      return list.length > 0 && list.includes(snapshot.locale);
    }
    case 'device': {
      const list = Array.isArray(opt['in']) ? (opt['in'] as string[]) : [];
      return list.length > 0 && list.includes(snapshot.device);
    }
    case 'userAuth': {
      const v = opt['visibility'];
      if (v === 'authenticated') return snapshot.isAuthenticated;
      if (v === 'anonymous') return !snapshot.isAuthenticated;
      return false;
    }
    case 'timeOfDay': {
      const start = (opt['start'] ?? opt['startTime'] ?? '00:00') as string;
      const end = (opt['end'] ?? opt['endTime'] ?? '23:59') as string;
      return minuteInRange(snapshot.minuteOfDay, parseHm(start, '00:00'), parseHm(end, '23:59'));
    }
    case 'weekday': {
      const days = Array.isArray(opt['selectedDays']) ? (opt['selectedDays'] as number[]) : [];
      return days.length > 0 && days.includes(snapshot.weekday);
    }
    case 'dateRange': {
      const start = typeof opt['start'] === 'string' ? opt['start'] : '';
      const end = typeof opt['end'] === 'string' ? opt['end'] : '';
      return dateInRange(snapshot.date, start, end);
    }
    case 'journeyParameter': {
      const paramKey = String(opt['paramKey'] ?? '').trim();
      if (!paramKey) return false;
      const val = snapshot.params[paramKey];
      const op = (opt['operator'] as string) ?? 'defined';
      if (op === 'defined' || op === 'exists') {
        return val != null && val !== '';
      }
      if (op === 'notDefined') {
        return val == null || val === '';
      }
      if (op === 'isTrue' || op === 'truthy') {
        return op === 'isTrue' ? val === true : Boolean(val);
      }
      if (op === 'isFalse' || op === 'falsy') {
        return op === 'isFalse' ? val === false : !Boolean(val);
      }
      if (op === 'equals' || op === 'eq') return val === opt['value'];
      if (op === 'notEquals' || op === 'neq') return val !== opt['value'];
      return val != null && val !== '';
    }
    case 'testMode': {
      const inTest = snapshot.isTestMode === true;
      if (opt['visibility'] === 'notInTestMode') return !inTest;
      return inTest;
    }
    case 'shop':
      return evaluateShopContextCondition(opt, snapshot);
    default:
      return false;
  }
}

export function selectContextualBranch(
  casesObj: ContextualCases,
  snapshot: RuntimeContextSnapshot,
): unknown {
  for (const entry of casesObj.$cases ?? []) {
    const when = entry?.when;
    if (!Array.isArray(when) || when.length === 0) continue;
    if (when.every(c => evaluateContextCondition(c, snapshot))) {
      return entry.value;
    }
  }
  return casesObj.default;
}

/** Single entry point — every $cases / $param resolution goes through here. */
export function resolveBoundValue(value: unknown, options: ResolveBoundValueOptions = {}): unknown {
  const maxDepth = options.maxDepth ?? MAX_DEPTH_DEFAULT;
  const depth = options.depth ?? 0;
  const lang = options.lang ?? options.context?.locale ?? 'fr';
  const context = options.context ?? buildRuntimeContextSnapshot({ locale: lang, params: {} });

  if (depth > maxDepth || value === null || value === undefined) {
    return value;
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const normalizedCases = normalizeStudioCasesToRuntime(value);
  if (normalizedCases !== value) {
    return resolveBoundValue(normalizedCases, { ...options, context, lang, depth: depth + 1 });
  }

  if (isContextualCases(value)) {
    if (options.preserveContextualCases) {
      return value;
    }
    const branch = selectContextualBranch(value, context);
    return resolveBoundValue(branch, { ...options, context, lang, depth: depth + 1 });
  }

  if (isParamRefValue(value)) {
    const refKey = readParamRefKeyFromValue(value);
    if (!refKey || typeof options.resolveParamRef !== 'function') return value;
    const resolved = options.resolveParamRef(refKey);
    return resolveBoundValue(resolved, { ...options, context, lang, depth: depth + 1 });
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = resolveBoundValue(v, { ...options, context, lang, depth: depth + 1 });
  }
  return out;
}
